import React, { useState } from 'react';
import styles from './ManagementProfile.module.scss';
import user from '../../../../../../assets/user.png';

import { ctaPurpleRight } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import ManagementUserProfile from './components/ManagementUserProfile';
import { toTitleCase } from '../../../../../../utils/utils';
import clsx from 'clsx';
import dayjs from 'dayjs';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import HeaderBodyBox from '../../../../../../common/HeaderBodyBox/HeaderBodyBox';
import AIGeneratedBadge from '../../../../../../common/AIGeneratedBadge/AIGeneratedBadge';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';

interface SingleProfile {
    age: string;
    designation: string;
    din: string;
    directorSince: string;
    name: string;
    imageUrl?: string;
    isActive?: boolean;
    linkedinUrl?: string;
    tenureBeginDate?: number;
    tenureEndDate?: number;
    managementProfileSummary?: string;
}

function ManagementProfile({ profiles, redirectToCreditBureau, redirectToCompliances, isPDFReport }: any) {
    const [showUserProfile, setShowUserProfile] = useState<null | SingleProfile>(null);

    if (!profiles?.length) {
        return <></>;
    }

    const headerRight = (
        <div style={{ display: 'flex', alignItems: 'center', columnGap: '1.875rem' }}>
            {!isPDFReport && (
                <AskAicaQuestionBadge question="Summarize the profile of the promoters" prompt="Summarize the profile of the promoters" />
            )}
            <AIGeneratedBadge />
        </div>
    );

    const render = (
        <HeaderBodyBox title="Management Profile" headerRight={headerRight}>
            <div className={clsx(styles.contentBody, { [styles.pdfReport]: isPDFReport })}>
                {profiles?.map((profile: SingleProfile, i) => (
                    <HeaderBodyBox
                        key={`${profile?.name}-${i}`}
                        title={
                            <div className={styles.profileImage}>
                                <img
                                    src={profile?.imageUrl || user}
                                    alt="profile"
                                    onError={({ currentTarget }) => {
                                        currentTarget.onerror = null;
                                        currentTarget.src = user;
                                    }}
                                />
                            </div>
                        }
                        className={styles.profileCard}
                        bodyClassName={styles.cardContent}
                        isPDFReport={isPDFReport}
                        headerClassName={styles.cardHeader}
                        headerRight={
                            <div className={styles.headerContent}>
                                <div className={styles.headerProfileInfo}>
                                    <div className={styles.profileName}>
                                        {profile?.name && profile?.name?.length > 40 ? (
                                            <Tooltip direction="bottom" content={profile?.name} fitContent>
                                                {`${toTitleCase(profile?.name?.substring?.(0, 40))}...`}
                                            </Tooltip>
                                        ) : (
                                            toTitleCase(profile?.name)
                                        )}
                                    </div>
                                    <div className={styles.headerMetaInfo}>
                                        {profile?.isActive !== null && profile?.isActive !== undefined && (
                                            <div
                                                className={clsx(styles.statusBadge, {
                                                    [styles.active]: profile?.isActive,
                                                    [styles.former]: !profile?.isActive,
                                                })}
                                            >
                                                {profile?.isActive ? 'Active' : 'Former'}
                                            </div>
                                        )}
                                        {/* Gender would go here if available in the profile object */}
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <div className={styles.profileDetails}>
                            <div className={styles.detailRow}>
                                <div className={styles.detailLabel}>Designation</div>
                                <div className={styles.detailValue}>{profile?.designation || '-'}</div>
                            </div>

                            <div className={styles.detailRow}>
                                <div className={styles.detailLabel}>Tenure</div>
                                <div className={styles.detailValue}>
                                    {(profile?.isActive === null || profile?.isActive === undefined) &&
                                    !profile?.tenureBeginDate &&
                                    !profile?.tenureEndDate ? (
                                        <>NA</>
                                    ) : (
                                        <>
                                            {profile?.tenureBeginDate && dayjs(profile?.tenureBeginDate).isValid()
                                                ? dayjs(profile?.tenureBeginDate).format('MMM YYYY')
                                                : ''}{' '}
                                            -{' '}
                                            {profile?.tenureEndDate && dayjs(profile?.tenureEndDate).isValid()
                                                ? dayjs(profile?.tenureEndDate).format('MMM YYYY')
                                                : profile?.isActive
                                                ? 'Present'
                                                : 'NA'}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className={styles.detailRow}>
                                <div className={styles.detailLabel}>Age</div>
                                <div className={styles.detailValue}>{profile?.age || '-'}</div>
                            </div>
                        </div>
                        <div className={styles.summary}>
                            <div className={styles.summaryValue}>{profile?.managementProfileSummary || 'Data not available'}</div>
                        </div>

                        <button className={styles.viewButton} onClick={() => setShowUserProfile(profile)}>
                            <img src={ctaPurpleRight} alt="" />
                            View Details
                        </button>
                    </HeaderBodyBox>
                ))}
            </div>

            {showUserProfile && (
                <SideDrawer onClose={() => setShowUserProfile(null)} width={'60%'}>
                    <ManagementUserProfile
                        showUserProfile={showUserProfile}
                        redirectToCreditBureau={redirectToCreditBureau}
                        redirectToCompliances={redirectToCompliances}
                    />
                </SideDrawer>
            )}
        </HeaderBodyBox>
    );

    if (isPDFReport)
        return (
            <>
                {!!profiles?.length && reportSectionsToShow?.includes(REPORT_SECTION_NAMES.MANAGEMENT_PROFILE_SUMMARY) ? (
                    <A4Page sectionName={REPORT_SECTION_NAMES.MANAGEMENT_PROFILE_SUMMARY}>{render}</A4Page>
                ) : (
                    <></>
                )}
                {reportSectionsToShow?.includes(REPORT_SECTION_NAMES.MANAGEMENT_PROFILE_INDIVIDUAL) && profiles?.length ? (
                    profiles?.map((profile: SingleProfile, i) => (
                        <A4Page key={i} sectionName={REPORT_SECTION_NAMES.MANAGEMENT_PROFILE_INDIVIDUAL}>
                            <ManagementUserProfile
                                showUserProfile={profile}
                                redirectToCreditBureau={redirectToCreditBureau}
                                redirectToCompliances={redirectToCompliances}
                                isPDFReport={isPDFReport}
                            />
                        </A4Page>
                    ))
                ) : (
                    <></>
                )}
            </>
        );
    else return render;
}

export default ManagementProfile;
